import React, { useMemo } from 'react'
import { ParsedUrlQuery } from 'querystring'
import { Redirect } from 'next'
import { useRouter } from 'next/router'
import omit from 'lodash/omit'
import {
  fetchNationalStaticData,
  NationalStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { getRedirectForMismatchedUrls } from '@scentregroup/shared/helpers/get-redirect-for-mismatched-urls'
import {
  GetWestfieldDirectRetailerDocument,
  GetWestfieldDirectRetailerQuery,
} from '@scentregroup/shared/graphql'
import { NATIONAL_CONFIG } from '@scentregroup/shared/config'
import { ApolloClient } from '@apollo/client'
import MarketplaceProductPage from '../../../../src/client/pages/product/marketplace'
import {
  GetServerSideProps,
  GetServerSidePropsContext,
} from '../../../../types/next'
import { makeApolloClient } from '../../../../lib/apolloClient'
import { useNotification } from '../../../../src/client/lib/use-notification'
import { useCookie, CookieName } from '../../../../src/client/lib/cookie'
import useTrackProductDetailsAnalytics from '../../../../src/client/lib/use-track-product-details-analytics'
import {
  TradingState,
  IGetMarketplaceProductResponse,
  IGetMarketplaceShopResponse,
  MarketplaceProduct,
} from '../../../../src/client/types/marketplace-product'
import { GetMarketplaceProduct } from '../../../../src/client/graphql/queries'
import WestfieldDirectLayout from '../../../../src/client/components/westfield-direct/layout'
import PageMetaData from '../../../../src/shared/component/page-meta-data'
import { VisualGuideContextProvider } from '../../../../src/client/lib/use-visual-guide-context'
import {
  getShippingDeadline,
  ShippingDeadlineResult,
} from '../../../../lib/get-shipping-deadlines'
import { checkNationalStorefront } from '../../../../lib/check-national-storefront'
import { transformParsedUrlQuery } from '@scentregroup/shared/helpers/url-helpers'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import useFeatureFlag from '@scentregroup/shared/hooks/use-feature-flag'
import { FeatureFlagName } from '@scentregroup/shared/feature-flags'

interface IMarketplaceProductParams extends ParsedUrlQuery {
  productId: string
  shopId: string
  slug: string
}

interface IMarketplaceProductProps {
  marketplaceProduct: IGetMarketplaceProductResponse
  marketplaceShop: IGetMarketplaceShopResponse
  directHeaderData: NationalStaticData
  topStripProps: ITopStripProps | null
  shippingCost: number
  shippingDeadline: ShippingDeadlineResult
  defaultRetailerSizeGuide: GetWestfieldDirectRetailerQuery
}

async function getClosedShopRedirect<TCacheShape>(
  client: Pick<ApolloClient<TCacheShape>, 'query'>,
  marketplaceShop: IGetMarketplaceShopResponse
): Promise<Redirect | undefined> {
  if (marketplaceShop.tradingState === TradingState.Open) {
    return undefined
  }
  const retailer = marketplaceShop.retailer
  const retailerId = retailer?.sys.id
  const permanent =
    marketplaceShop.tradingState === TradingState.ClosedPermanently
  const hasNationalStorefront =
    !permanent &&
    retailerId &&
    (await checkNationalStorefront(client, retailerId))

  const e = encodeURIComponent
  const destination = hasNationalStorefront
    ? `/store/${e(retailerId)}/${e(retailer.slug)}`
    : '/'
  return {
    basePath: false,
    destination,
    permanent,
  }
}

function getRedirectForProductWithMismatchedSlug(
  context: GetServerSidePropsContext,
  marketplaceProduct: IGetMarketplaceProductResponse,
  marketplaceShop: IGetMarketplaceShopResponse
): Redirect | null {
  const { query, locale } = context

  const META_INFORMATION =
    locale === 'en-NZ' ? NATIONAL_CONFIG.nz : NATIONAL_CONFIG.au
  const url = `${META_INFORMATION.url}/shop${context.resolvedUrl}`
  const urlQuery = omit(query, 'shopId', 'productId', 'slug')
  const product = new MarketplaceProduct(
    marketplaceProduct,
    marketplaceShop,
    url,
    new URLSearchParams(transformParsedUrlQuery(urlQuery))
  )
  if (!product.url) {
    return null
  }
  return getRedirectForMismatchedUrls(url, product.url)
}

export const getServerSideProps: GetServerSideProps<
  IMarketplaceProductProps,
  IMarketplaceProductParams
> = async context => {
  const { params, locale, req, res, resolvedUrl } = context
  setCacheHeaders(res, 'product-details')

  const { productId = '', shopId = '', slug = '' } = params ?? {}

  const client = makeApolloClient({
    cookies: req.cookies,
  })

  const { data: defaultRetailerSizeGuide } = await client.query({
    context: { version: 2 },
    query: GetWestfieldDirectRetailerDocument,
  })

  const country = localeToCountry(locale)
  const [
    directHeaderData,
    { data: productData },
    shippingDeadline,
    topStripProps,
  ] = await Promise.all([
    fetchNationalStaticData(client, { country }),
    client.query<{
      marketplaceProduct: IGetMarketplaceProductResponse | null
      marketplaceShop: IGetMarketplaceShopResponse | null
      marketplaceShippingConfiguration: {
        shippingCost: number
      }
    }>({
      query: GetMarketplaceProduct,
      context: { version: 2 },
      variables: {
        id: productId,
        shop: parseInt(shopId, 10),
      },
      errorPolicy: 'all',
    }),
    getShippingDeadline(client),
    getTopStripProps(client, country),
  ])

  if (!productData.marketplaceShop) {
    return {
      notFound: true,
    }
  }
  const closedShopRedirect = await getClosedShopRedirect(
    client,
    productData.marketplaceShop
  )
  if (closedShopRedirect) {
    return {
      redirect: closedShopRedirect,
    }
  }
  if (!productData.marketplaceProduct) {
    return {
      notFound: true,
    }
  }
  const variantGroupCode =
    productData.marketplaceProduct.variants?.[0]?.variantGroupCode
  if (variantGroupCode && productId !== variantGroupCode) {
    const destination = new URL(resolvedUrl, 'null:/')
    const e = encodeURIComponent
    destination.pathname = `/product/${e(shopId)}/${e(variantGroupCode)}/${e(
      slug
    )}`
    destination.searchParams.set('v', productId)
    return {
      redirect: {
        destination: `${destination.pathname}${destination.search}`,
        permanent: false,
      },
    }
  }

  const redirect = getRedirectForProductWithMismatchedSlug(
    context,
    productData.marketplaceProduct,
    productData.marketplaceShop
  )
  if (redirect) {
    return { redirect }
  }

  return {
    props: {
      marketplaceShop: productData.marketplaceShop,
      marketplaceProduct: productData.marketplaceProduct,
      shippingCost: productData.marketplaceShippingConfiguration.shippingCost,
      shippingDeadline,
      defaultRetailerSizeGuide,
      directHeaderData,
      topStripProps,
    },
  }
}

const ProductPage: React.FC<React.PropsWithChildren<IMarketplaceProductProps>> = ({
  marketplaceProduct,
  marketplaceShop,
  shippingCost,
  shippingDeadline,
  defaultRetailerSizeGuide,
}) => {
  const router = useRouter()
  const onNotification = useNotification()
  const [cartId, setCartId] = useCookie(CookieName.CART)
  const META_INFORMATION =
    router.locale === 'en-NZ' ? NATIONAL_CONFIG.nz : NATIONAL_CONFIG.au

  const product = useMemo(
    () =>
      new MarketplaceProduct(
        marketplaceProduct,
        marketplaceShop,
        `${META_INFORMATION.url}${router.basePath}${router.asPath}`
      ),
    [
      META_INFORMATION.url,
      marketplaceProduct,
      marketplaceShop,
      router.asPath,
      router.basePath,
    ]
  )

  const retailerSizeGuide = marketplaceShop.retailer?.retailerSizeGuide
  const defaultSizeGuide =
    defaultRetailerSizeGuide.cfRetailerCollection?.items[0]?.retailerSizeGuide
  const productNameWithBrand =
    !product.brand || product.title.includes(product.brand)
      ? product.title
      : `${product.brand} ${product.title}`
  const enableBrandUpdate = useFeatureFlag(
    FeatureFlagName.NEXT_PUBLIC_FEAT_FLAG_UPDATED_BRAND
  )
  const brandText = enableBrandUpdate ? 'Westfield' : 'Westfield Direct'
  useTrackProductDetailsAnalytics(product)

  return (
    <VisualGuideContextProvider>
      <PageMetaData
        getMetaData={() => ({
          title: `${productNameWithBrand} | ${product.shop.name} | Shop online at ${brandText}`,
          description: `Shop ${productNameWithBrand} online from ${product.shop.name} at ${brandText}. Free and easy returns. Click & Collect or delivery available.`,
          structuredData: product.structuredData(
            (Array.isArray(router.query.v)
              ? router.query.v[0]
              : router.query.v) ?? ''
          ),
          canonical: product.canonicalUrl,
          showRobotsMetaTag: true,
        })}
      />
      <MarketplaceProductPage
        key={product.id}
        product={product}
        onNotification={onNotification}
        cartId={cartId}
        setCartId={setCartId}
        shippingCost={shippingCost}
        shippingDeadline={shippingDeadline}
        retailerSizeGuide={retailerSizeGuide}
        defaultSizeGuide={defaultSizeGuide}
      />
    </VisualGuideContextProvider>
  )
}

const Page: React.FC<React.PropsWithChildren<IMarketplaceProductProps>> = props => {
  const { directHeaderData, topStripProps, ...restProps } = props
  return (
    <WestfieldDirectLayout
      component={ProductPage}
      restProps={{ ...restProps }}
      directHeaderData={directHeaderData}
      topStrip={topStripProps}
      noTopPadding
    />
  )
}

export default Page
